/**
 * Comentarios de una tarea. En el historial son los eventos de tipo `NOTA` (el nombre técnico no
 * cambia); aquí se muestran como conversación, aparte del resto del historial.
 *
 * Comentar lo puede hacer cualquiera con permiso de escritura, también quien no creó la tarea:
 * es la vía para pedirle un cambio a quien sí puede editar el requerimiento.
 */
import { useState } from 'react';
import type { EventoTarea } from '@cdtec-backlog/contrato';
import { Avatar, nombreCorto } from '../../componentes/Avatar';
import { Giro } from '../../componentes/Esqueleto';
import { fechaHora } from '../../lib/formato';
import { useAgregarNota } from './hooks';
import './Comentarios.css';

export const MAX_COMENTARIO = 1000;

/** Los comentarios son las notas; ordenados del más antiguo al más nuevo. */
export const soloComentarios = (eventos: EventoTarea[]) => eventos.filter((e) => e.tipo === 'NOTA');

interface Props {
  tareaId: number;
  eventos: EventoTarea[];
  puedeEscribir: boolean;
  /** Nombre de quien mira, para el avatar de la caja de comentario. */
  yoNombre?: string;
}

export function Comentarios({ tareaId, eventos, puedeEscribir, yoNombre }: Props) {
  const [texto, setTexto] = useState('');
  const agregar = useAgregarNota(tareaId);
  const comentarios = soloComentarios(eventos);
  const vacio = texto.trim() === '';

  return (
    <section className="comentarios" aria-labelledby="comentarios-titulo">
      <h3 id="comentarios-titulo" className="seccion-titulo">
        Comentarios
      </h3>
      {puedeEscribir && (
        <form
          className="comentarios__form"
          onSubmit={(evento) => {
            evento.preventDefault();
            if (vacio) return;
            agregar.mutate(texto.trim(), { onSuccess: () => setTexto('') });
          }}
        >
          <div className="comentarios__caja">
            {yoNombre && <Avatar nombre={yoNombre} grande />}
            <div className="comentarios__entrada">
              <label className="solo-lectores" htmlFor="comentario-nuevo">
                Comentar o pedir un cambio al creador
              </label>
              <textarea
                id="comentario-nuevo"
                value={texto}
                placeholder="Escribe un comentario o pide un cambio al creador…"
                onChange={(e) => setTexto(e.target.value)}
                maxLength={MAX_COMENTARIO}
                aria-describedby="comentario-contador"
              />
              <div className="comentarios__pie">
                <span id="comentario-contador" className="comentarios__contador">
                  {texto.length}/{MAX_COMENTARIO}
                </span>
                <button
                  type="submit"
                  className="boton boton--primario boton--con-giro"
                  disabled={agregar.isPending || vacio}
                >
                  {agregar.isPending && <Giro />}
                  {agregar.isPending ? 'Enviando…' : 'Comentar'}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
      {comentarios.length === 0 ? (
        <p className="comentarios__vacio">Aún no hay comentarios.</p>
      ) : (
        <ul className="comentarios__lista">
          {comentarios.map((comentario) => (
            <li key={comentario.id}>
              {/* Sin autor (p. ej. una nota importada) igual lleva avatar («?»): queda alineado con el resto. */}
              <Avatar nombre={comentario.autor?.nombre ?? ''} grande />
              <div className="comentarios__contenido">
                <p className="comentarios__autor">
                  <strong title={comentario.autor?.nombre}>
                    {comentario.autor ? nombreCorto(comentario.autor.nombre) : 'Sin autor'}
                  </strong>{' '}
                  <time dateTime={comentario.creadoEn}>{fechaHora(comentario.creadoEn)}</time>
                </p>
                <p className="comentarios__texto">{comentario.texto}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
